import {
  CreditCardOutlined,
  ExportOutlined,
  FlagOutlined,
  PieChartOutlined,
  ProjectOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  SwapOutlined
} from "@ant-design/icons"
import { Button, Flex, Skeleton, Space, Tag, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DebouncedInput } from "../../../../../../../components/Input/DebouncedInput.tsx"
import { ENTRY_TYPE_OPTIONS } from "../../../../constants/finance-item-constants.ts"
import { type ProjectFinancialTransactionFilters } from "../../../../../redux/project.types.ts"
import { type ProjectSubcontractor } from "../../../../../types/project-subcontractors.types.tsx"
import {
  FilterDropdownButton,
  MultiSelectFilterPanel
} from "./finance-filter-controls.tsx"
import {
  deriveMilestoneOptions,
  pruneSelectedMilestones,
  type ProjectTypeFilterOption
} from "./finance-filter-helpers.ts"

const FILTER_KEYS = [
  "item_type",
  "entry_type",
  "project_type",
  "project_phase",
  "status"
] as const

// Compares by value so two independently-pruned arrays with the same ids don't count as changed.
const sameIdArray = (a: string[] | null, b: string[] | null): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return a.length === b.length && a.every((id, index) => id === b[index])
}

interface ProjectViewFinanceHeaderProps {
  selectedSubcontractor: ProjectSubcontractor | null
  filters: ProjectFinancialTransactionFilters
  isEmptyList: boolean
  options: Record<string, BaseOptionType[]>
  isLocked?: boolean
  fetchingOptions: boolean
  onFilterChange: (data: Partial<ProjectFinancialTransactionFilters>) => void
  onExport: () => Promise<void>
  onClearSubcontractor: () => void
  onOpenColumnConfig: () => void
}

export function ProjectViewFinanceHeader({
  selectedSubcontractor,
  filters,
  options,
  isEmptyList,
  isLocked = false,
  fetchingOptions,
  onFilterChange,
  onExport,
  onClearSubcontractor,
  onOpenColumnConfig
}: ProjectViewFinanceHeaderProps) {
  const { t } = useTranslation()
  const hasAdditionalFilters = !!selectedSubcontractor
  const disabled = isLocked || hasAdditionalFilters
  const itemTypeOptions = useMemo(
    () =>
      (options?.["item_type"] ?? []).map(({ label, value }) => ({
        label,
        value: value as number
      })),
    [options]
  )

  const entryTypeOptions = useMemo(() => {
    const source = options?.["entry_type"]?.length
      ? options["entry_type"]
      : ENTRY_TYPE_OPTIONS
    return source.map(({ label, value }) => ({
      label,
      value: value as string
    }))
  }, [options])

  const projectTypeOptions: ProjectTypeFilterOption[] = useMemo(
    () =>
      (options?.["project_type"] ?? []).map((option) => ({
        label: option.label as string,
        value: option.value as string,
        milestones: option.milestones as
          | { label: string; value: string }[]
          | undefined
      })),
    [options]
  )

  const milestoneOptions = useMemo(
    () => deriveMilestoneOptions(projectTypeOptions, filters?.project_type),
    [projectTypeOptions, filters?.project_type]
  )

  const statusOptions = useMemo(
    () =>
      (options?.["status"] ?? []).map(({ label, value }) => ({
        label,
        value
      })),
    [options]
  )

  // One shared key (not a boolean per pill) is what keeps only one panel open at a time.
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null)

  // Built once via useState's stable setter, not inline, so FilterDropdownButton's effect
  // doesn't see a "new" onOpenChange (and re-register its listeners) on every render.
  const [onOpenChangeByKey] = useState<Record<string, (open: boolean) => void>>(
    () =>
      Object.fromEntries(
        FILTER_KEYS.map((key) => [
          key,
          (open: boolean) => setOpenFilterKey(open ? key : null)
        ])
      )
  )

  // Counts only ids still present in options, so a project type/milestone deleted server-side
  // while the page was open doesn't inflate the badge with no way to see or clear it.
  const activeProjectTypeCount = (filters?.project_type ?? []).filter((id) =>
    projectTypeOptions.some((option) => option.value === id)
  ).length
  const activeMilestoneCount = (filters?.project_phase ?? []).filter((id) =>
    milestoneOptions.some((option) => option.value === id)
  ).length

  const handleItemTypeChange = (value: number[]) => {
    onFilterChange({ item_type: value.length ? value : null })
  }

  const handleEntryTypeChange = (value: string[]) => {
    onFilterChange({ entry_type: value.length ? value : null })
  }

  const handleProjectTypeChange = (value: string[]) => {
    const nextProjectType = value.length ? value : null
    const nextProjectPhase = pruneSelectedMilestones(
      filters?.project_phase,
      deriveMilestoneOptions(projectTypeOptions, nextProjectType)
    )

    // Only patch project_phase when narrowing actually pruned something, not on every toggle.
    const patch: Partial<ProjectFinancialTransactionFilters> = {
      project_type: nextProjectType
    }
    if (!sameIdArray(nextProjectPhase, filters?.project_phase ?? null)) {
      patch.project_phase = nextProjectPhase
    }
    onFilterChange(patch)
  }

  const handleMilestoneChange = (value: string[]) => {
    onFilterChange({ project_phase: value.length ? value : null })
  }

  const handleStatusChange = (value: number[]) => {
    onFilterChange({ status: value.length ? value : null })
  }

  // Bumped on reset so DebouncedInput clears even when `q` was already null (no visible change).
  const [searchResetSignal, setSearchResetSignal] = useState(0)

  const handleResetFilters = () => {
    setSearchResetSignal((n) => n + 1)
    onFilterChange({
      item_type: null,
      entry_type: null,
      project_type: null,
      project_phase: null,
      status: null,
      q: null
    })
  }

  return (
    <Flex gap="small" vertical>
      <Flex justify="space-between" align="center">
        <Typography.Title level={4}>
          Financial Transactions Items
        </Typography.Title>

        <Space>
          <DebouncedInput
            disabled={disabled}
            className={"w-64"}
            placeholder="Search Items..."
            value={filters?.q ?? null}
            resetSignal={searchResetSignal}
            prefix={<SearchOutlined />}
            onValueChange={(value) => onFilterChange({ q: value })}
          />
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={onOpenColumnConfig}
            disabled={isLocked}
          >
            {t("entityTable.customize")}
          </Button>
          <ExportButton
            onClick={onExport}
            disabled={isLocked || isEmptyList || hasAdditionalFilters}
          />
        </Space>
      </Flex>

      {fetchingOptions ? (
        <Skeleton.Node active className={"h-8 w-full"} />
      ) : (
        <Flex gap={8} align="center" wrap>
          <FilterDropdownButton
            icon={<SwapOutlined />}
            label={t("finance.filters.transactionType")}
            disabled={disabled}
            activeCount={filters?.item_type?.length ?? 0}
            open={openFilterKey === "item_type"}
            onOpenChange={onOpenChangeByKey.item_type}
          >
            {() => (
              <MultiSelectFilterPanel
                options={itemTypeOptions}
                value={filters?.item_type ?? []}
                onChange={handleItemTypeChange}
              />
            )}
          </FilterDropdownButton>

          <FilterDropdownButton
            icon={<CreditCardOutlined />}
            label={t("finance.filters.paymentType")}
            disabled={disabled}
            activeCount={filters?.entry_type?.length ?? 0}
            open={openFilterKey === "entry_type"}
            onOpenChange={onOpenChangeByKey.entry_type}
          >
            {() => (
              <MultiSelectFilterPanel
                options={entryTypeOptions}
                value={filters?.entry_type ?? []}
                onChange={handleEntryTypeChange}
              />
            )}
          </FilterDropdownButton>

          <FilterDropdownButton
            icon={<ProjectOutlined />}
            label={t("finance.filters.projectType")}
            disabled={disabled}
            activeCount={activeProjectTypeCount}
            open={openFilterKey === "project_type"}
            onOpenChange={onOpenChangeByKey.project_type}
          >
            {() => (
              <MultiSelectFilterPanel
                options={projectTypeOptions}
                value={filters?.project_type ?? []}
                onChange={handleProjectTypeChange}
              />
            )}
          </FilterDropdownButton>

          <FilterDropdownButton
            icon={<FlagOutlined />}
            label={t("finance.filters.milestone")}
            disabled={disabled}
            activeCount={activeMilestoneCount}
            open={openFilterKey === "project_phase"}
            onOpenChange={onOpenChangeByKey.project_phase}
          >
            {() => (
              <MultiSelectFilterPanel
                options={milestoneOptions}
                value={filters?.project_phase ?? []}
                onChange={handleMilestoneChange}
              />
            )}
          </FilterDropdownButton>

          <FilterDropdownButton
            icon={<PieChartOutlined />}
            label={t("finance.filters.status")}
            disabled={disabled}
            activeCount={filters?.status?.length ?? 0}
            open={openFilterKey === "status"}
            onOpenChange={onOpenChangeByKey.status}
          >
            {() => (
              <MultiSelectFilterPanel
                options={statusOptions}
                value={filters?.status ?? []}
                onChange={handleStatusChange}
              />
            )}
          </FilterDropdownButton>

          <Button
            icon={<ReloadOutlined />}
            disabled={disabled}
            onClick={handleResetFilters}
            className="!h-10 !border-primary !text-primary"
          >
            {t("finance.filters.resetFilters")}
          </Button>

          {selectedSubcontractor && (
            <Tag closable onClose={onClearSubcontractor} color="blue">
              {selectedSubcontractor?.full_name || "No name provided"}
            </Tag>
          )}
        </Flex>
      )}
    </Flex>
  )
}

interface ExportButtonProps {
  disabled?: boolean
  onClick?: () => Promise<void>
}

const ExportButton = ({ onClick, disabled }: ExportButtonProps) => {
  const [exporting, setExporting] = useState(false)

  const onClickHandler = async () => {
    try {
      setExporting(true)
      await onClick?.()
    } finally {
      setExporting(false)
    }
  }

  return (
    <Button
      size="small"
      loading={exporting}
      type="primary"
      icon={<ExportOutlined />}
      disabled={disabled}
      onClick={onClickHandler}
    >
      Export
    </Button>
  )
}
